import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import type { Toast } from 'primereact/toast';
import type { Employee, Task } from 'types/interfaces';

import { TaskService } from 'components/services/TaskService';

function requiredFromTaskType(task: Task): number | null {
  const n = task.taskType?.requiredEmployees;
  if (typeof n === 'number' && n > 0 && !Number.isNaN(n)) return n;
  return null;
}

type AssignConfirmPayload = {
  taskId: number;
  employees: Employee[];
  recommended: number | null;
};

export function useManualEmployeeAssignment({
  visible,
  toastRef,
  reloadTasks
}: {
  visible: boolean;
  toastRef: RefObject<Toast | null>;
  reloadTasks: () => Promise<void> | void;
}) {
  const [manualExecutorsByTaskId, setManualExecutorsByTaskId] = useState<
    Record<number, Employee[]>
  >({});
  const [assignConfirm, setAssignConfirm] =
    useState<AssignConfirmPayload | null>(null);
  const assignConfirmRef = useRef<AssignConfirmPayload | null>(null);

  useEffect(() => {
    assignConfirmRef.current = assignConfirm;
  }, [assignConfirm]);

  useEffect(() => {
    if (!visible) return;
    setManualExecutorsByTaskId({});
    setAssignConfirm(null);
  }, [visible]);

  const handleManualExecutorsChange = useCallback(
    (taskId: number, next: Employee[]) => {
      setManualExecutorsByTaskId((prev) => ({
        ...prev,
        [taskId]: Array.isArray(next) ? [...next] : []
      }));
    },
    []
  );

  const persistManualAssignment = useCallback(
    async (taskId: number, employees: Employee[]) => {
      try {
        const employeeIds = employees
          .map((e) => Number(e.id))
          .filter((n) => !isNaN(n) && n > 0);
        await TaskService.updateTaskEmployees(taskId, employeeIds);
        toastRef.current?.show({
          severity: 'success',
          summary: 'Назначение сохранено',
          detail: `Исполнителей: ${employeeIds.length}`,
          life: 3500
        });
        setManualExecutorsByTaskId((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        await reloadTasks();
      } catch (e: any) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Ошибка сохранения',
          detail: e?.message ?? 'Не удалось сохранить назначение',
          life: 5000
        });
      }
    },
    [toastRef, reloadTasks]
  );

  const dismissAssignEmployeeConfirm = useCallback(() => {
    setAssignConfirm(null);
  }, []);

  const confirmAssignEmployee = useCallback(async () => {
    const pending = assignConfirmRef.current;
    if (!pending) return;
    setAssignConfirm(null);
    await persistManualAssignment(pending.taskId, pending.employees);
  }, [persistManualAssignment]);

  const onManualAssign = useCallback(
    (task: Task) => {
      const taskId = Number(task.id);
      const employees = manualExecutorsByTaskId[taskId] ?? [];
      if (!employees.length) return;

      const openConfirm = async () => {
        let required = requiredFromTaskType(task);
        if (required === null) {
          try {
            const resp = await TaskService.getRequiredEmployeeCount(taskId);
            const v = resp?.data?.value;
            if (typeof v === 'number' && v > 0 && !Number.isNaN(v)) {
              required = v;
            }
          } catch {
            required = null;
          }
        }
        setAssignConfirm({
          taskId,
          employees,
          recommended: required
        });
      };

      void openConfirm();
    },
    [manualExecutorsByTaskId]
  );

  const assignEmployeeConfirmMessage =
    assignConfirm === null
      ? ''
      : `Рекомендованное количество исполнителей для выбранного типа наряда: ${
          assignConfirm.recommended ?? 0
        }. Текущее количество: ${assignConfirm.employees.length}.`;

  return {
    manualExecutorsByTaskId,
    handleManualExecutorsChange,
    onManualAssign,
    assignEmployeeConfirmOpen: assignConfirm !== null,
    assignEmployeeConfirmMessage,
    dismissAssignEmployeeConfirm,
    confirmAssignEmployee
  };
}
